// Reloads from IndexedDB build every project/list/task as a new object. Keeping the previous object wherever the
// content is unchanged lets the view reuse what it rendered for it (typing while colleagues' changes stream in).
import type { Project, Task, TaskList } from '@/domain/model';

const enc = (o: object): string => JSON.stringify(o); // not cached: an object changed in place must never look unchanged

/** `next` with every project, list and task that equals its counterpart in `prev` replaced by that previous object. */
export function shareFrom(prev: Project[], next: Project[]): Project[] {
  const oldTasks = new Map<string, Task>(), oldLists = new Map<string, TaskList>(), oldProjects = new Map<string, Project>();
  for (const p of prev) { oldProjects.set(p.id, p); for (const l of p.lists) { oldLists.set(l.id, l); for (const t of l.tasks) oldTasks.set(t.id, t); } }
  let same = prev.length === next.length;
  const out = next.map((p, pi) => {
    const op = oldProjects.get(p.id);
    let listsSame = !!op && op.lists.length === p.lists.length;
    const lists = p.lists.map((l, li) => {
      const ol = oldLists.get(l.id);
      let tasksSame = !!ol && ol.tasks.length === l.tasks.length;
      const tasks = l.tasks.map((t, ti) => {
        const ot = oldTasks.get(t.id);
        const keep = ot && enc(ot) === JSON.stringify(t) ? ot : t;
        if (!ol || ol.tasks[ti] !== keep) tasksSame = false;
        return keep;
      });
      const kept = ol && tasksSame && ol.id === l.id && ol.title === l.title && ol.rank === l.rank && ol.revision === l.revision ? ol : { ...l, tasks };
      if (!op || op.lists[li] !== kept) listsSame = false;
      return kept;
    });
    if (op && listsSame && enc({ ...op, lists: null }) === JSON.stringify({ ...p, lists: null })) { if (prev[pi] !== op) same = false; return op; }
    same = false;
    return { ...p, lists };
  });
  return same ? prev : out;
}
